/**
 * @file dashboardStaffView.jsx
 * @author Daniel Aguirre
 * @description Staff Dashboard View
 */

// Dependencies
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Helpers
import { getStorageItem } from 'commons/helpers/storage-helper';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';
import DashboardTabMenu from 'components/Charts/DashboardTabMenu';
import DashboardCard from 'components/DashboardCard';

import AlbuminuriaProgress from './components/charts/AlbuminuriaProgress';
import AlbuminuriaF13 from './components/charts/AlbuminuriaF13';
import AntiBodyAntiHBs from './components/charts/AntiBodyAntiHBs';
import IndiBloodPressureMonitoring from './components/charts/IndiBloodPressureMonitoring';
import IndiGlucoseMonitoring from './components/charts/IndiGlucoseMonitoring';
import Hemoglobin from './components/charts/Hemoglobin';
import NutritionalIndicator from './components/charts/NutritionalIndicator';
import Potassium from './components/charts/Potassium';
import MetabolicControlDiabetes from './components/charts/MetabolicControlDiabetes';
import RecommendedByScheduling from './components/charts/RecommendedByScheduling';
import MissedAppointmentsByStage from './components/charts/MissedAppointmentsByStage';
import TFGDeterioration from './components/charts/TFGDeterioration';
import Phosphorus from './components/charts/Phosphorus';
import Albumina from './components/charts/Albumina';
import PthIndicator from './components/charts/PthIndicator';
import SeroStatusHepatitisB from './components/charts/SeroStatusHepatitisB';
import Risk from './components/charts/Risk';
import VaccineScheduling from './components/charts/VaccineScheduling';
import PostVaccineAntiHbs from './components/charts/PostVaccineAntiHbs';
import Access from './components/charts/Access';
import RiskRegister from './components/charts/RiskRegister';
import LossHepatitisImmunity from './components/charts/LossHepatitisImmunity';
import CorrelationRiskRateProgression from './components/charts/CorrelationRiskRateProgression';
import BloodPressure from './components/charts/BloodPressure';
import TreatmentOptions from './components/charts/TreatmentOptions';
import PsychosocialRiskPsycology from './components/charts/PsychosocialRiskPsycology';
// import AssesmentInPsychology from './components/charts/AssesmentInPsychology';
import NursingEducation from './components/charts/NursingEducation';
import SchedulingAlert from './components/charts/SchedulingAlert';
import ProteinEnergeticWear from './components/charts/ProteinEnergeticWear';
import GeneralSocialTSocialRisk from './components/charts/GeneralSocialTSocialRisk';
import DissentWithoutPsychologyAssessment from './components/charts/DissentWithoutPsychologyAssessment';
import PatientEvents from './components/charts/PatientEvents';
// import PeriodicityAttention from './components/charts/PeriodicityAttention';
import ConstructionAccessAssessment from './components/charts/ConstructionAccessAssessment';
import PsychosocialRisk from './components/charts/PsychosocialRisk';
import GeneralSocialRiskRegister from './components/charts/GeneralSocialRiskRegister';
// import FollowUpAbsenteeismAdherence from './components/charts/FollowUpAbsenteeismAdherence';
import Proteinuria from './components/charts/Proteinuria';

const profiles = {
  NEPHROLOGIST: 'Nefrólogo',
  NUTRITION: 'Nutrición',
  PSYCHOLOGY: 'Psicología',
  NURSING: 'Enfermería',
  MEDICALDIRECTOR: 'Director Médico',
  ADMISSIONS: 'Admisiones',
  SOCIAL_WORK: 'Trabajo Social'
};

const DashboardStaffView = ({ clinicId }) => {
  const [profile, setProfile] = useState(null);
  useEffect(async () => {
    const user = await getStorageItem('user');
    setProfile(user?.profile?.name);
  }, [clinicId]);

  const laboratorioTabtMenu1 = [
    { key: '0', value: 'Albúmina', title: 'Estadíos 1 a 5' },
    { key: '1', value: 'Hemoglobina', title: 'Estadíos 3 a 5' },
    { key: '2', value: 'Fósforo', title: 'Estadíos 4 y 5' },
    { key: '3', value: 'Potasio', title: 'Estadíos 3 a 5' },
    { key: '4', value: 'PTH', title: '' }
  ];

  const laboratorioTabtMenu2 = [
    { key: '0', value: 'Hemoglobina', title: 'Estadíos 3 a 5' },
    { key: '1', value: 'Fósforo', title: 'Estadíos 4 y 5' },
    { key: '2', value: 'Potasio', title: 'Estadíos 3 a 5' }
  ];

  const monitoreoAmbulatorioTab = [
    {
      key: '0',
      value: 'Tensión arterial'
    },
    {
      key: '1',
      value: 'Glucometría'
    }
  ];

  return (
    <AppCardLayout bgColor="bg-secondary">
      <>
        {clinicId !== null ? (
          <div className="flex flex-wrap h-full overflow-y-auto">
            {/* F6 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.PSYCHOLOGY ||
              profile === profiles.NURSING ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.ADMISSIONS ||
              profile === profiles.SOCIAL_WORK) && <RecommendedByScheduling />}

            {/* F25 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.PSYCHOLOGY ||
              profile === profiles.NURSING ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.ADMISSIONS ||
              profile === profiles.SOCIAL_WORK) && <SchedulingAlert />}

            {/* F7 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.PSYCHOLOGY ||
              profile === profiles.NURSING ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.ADMISSIONS ||
              profile === profiles.SOCIAL_WORK) && (
              <MissedAppointmentsByStage />
            )}

            {/* F8 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <SeroStatusHepatitisB />}

            {/* F9 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <VaccineScheduling />}

            {/* F10 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <AntiBodyAntiHBs />}

            {/* F11 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <PostVaccineAntiHbs />}

            {/* F12 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <LossHepatitisImmunity />}

            {/* F13 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <AlbuminuriaF13 />}

            {/* F14 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <AlbuminuriaProgress />}

            {/* F15 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <Proteinuria />}

            {/* F16 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.PSYCHOLOGY ||
              profile === profiles.NURSING ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.SOCIAL_WORK) && <TFGDeterioration />}

            {/* F17 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.PSYCHOLOGY ||
              profile === profiles.NURSING ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.SOCIAL_WORK) && <Risk />}

            {/* F18 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && (
              <CorrelationRiskRateProgression />
            )}

            {/* F19 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.MEDICALDIRECTOR) && <RiskRegister />}

            {/* F20 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <BloodPressure />}

            {/* F21 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <MetabolicControlDiabetes />}

            {/* F22 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <Access />}

            {/* F23 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.PSYCHOLOGY ||
              profile === profiles.NURSING ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.SOCIAL_WORK) && <TreatmentOptions />}

            {/* F24 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.SOCIAL_WORK ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <ConstructionAccessAssessment />}

            {/* F26 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <ProteinEnergeticWear />}

            {/* F27 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <NutritionalIndicator />}

            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && (
              <DashboardCard id="dashboard-lab">
                {profile === profiles.NEPHROLOGIST ||
                profile === profiles.NUTRITION ||
                profile === profiles.MEDICALDIRECTOR ||
                profile === profiles.NURSING ? (
                  <DashboardTabMenu menuItems={laboratorioTabtMenu1}>
                    {/* F28 */}
                    <Albumina />

                    {/* F29 */}
                    <Hemoglobin />

                    {/* F30 */}
                    <Phosphorus />

                    {/* F31 */}
                    <Potassium />

                    {/* F32 */}
                    <PthIndicator />
                  </DashboardTabMenu>
                ) : (
                  <DashboardTabMenu menuItems={laboratorioTabtMenu2}>
                    {/* F29 */}
                    <Hemoglobin />

                    {/* F30 */}
                    <Phosphorus />

                    {/* F31 */}
                    <Potassium />
                  </DashboardTabMenu>
                )}
              </DashboardCard>
            )}

            {/* F33 */}
            {(profile === profiles.NUTRITION ||
              profile === profiles.NEPHROLOGIST ||
              profile === profiles.SOCIAL_WORK ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && <NursingEducation />}

            {/* F34 */}
            {(profile === profiles.PSYCHOLOGY ||
              profile === profiles.MEDICALDIRECTOR) && (
              <PsychosocialRiskPsycology />
            )}

            {/* F35 */}
            {(profile === profiles.PSYCHOLOGY ||
              profile === profiles.MEDICALDIRECTOR) && <PsychosocialRisk />}

            {/* F36 */}
            {(profile === profiles.PSYCHOLOGY ||
              profile === profiles.MEDICALDIRECTOR) && (
              <DissentWithoutPsychologyAssessment />
            )}

            {/* F37 */}
            {/* {profile === profiles.PSYCHOLOGY && <AssesmentInPsychology />} */}

            {/* F38 */}
            {(profile === profiles.SOCIAL_WORK ||
              profile === profiles.MEDICALDIRECTOR) && (
              <GeneralSocialTSocialRisk />
            )}

            {/* F42 */}
            {(profile === profiles.SOCIAL_WORK ||
              profile === profiles.MEDICALDIRECTOR) && (
              <GeneralSocialRiskRegister />
            )}

            {/* F39 */}
            {/* (profile === profiles.SOCIAL_WORK ||
              profile === profiles.MEDICALDIRECTOR) && (
              <FollowUpAbsenteeismAdherence />
              ) */}

            {/* F40 */}
            {/* {profile === profiles.SOCIAL_WORK && <PeriodicityAttention />} */}

            {/* F41 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.PSYCHOLOGY ||
              profile === profiles.NURSING ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.ADMISSIONS ||
              profile === profiles.SOCIAL_WORK) && <PatientEvents />}

            {/* F43 */}
            {(profile === profiles.NEPHROLOGIST ||
              profile === profiles.NUTRITION ||
              profile === profiles.MEDICALDIRECTOR ||
              profile === profiles.NURSING) && (
              <DashboardCard id="F43">
                <DashboardTabMenu menuItems={monitoreoAmbulatorioTab}>
                  <IndiBloodPressureMonitoring />
                  <IndiGlucoseMonitoring />
                </DashboardTabMenu>
              </DashboardCard>
            )}
          </div>
        ) : (
          <div></div>
        )}
      </>
    </AppCardLayout>
  );
};

/**
 * @name PageHeader.propTypes
 * @type { Object }
 */
DashboardStaffView.propTypes = {
  clinicId: PropTypes.any
};
export default DashboardStaffView;
